// ==================== hooks/useInfiniteScroll.js ====================
import { useEffect, useRef, useCallback, useState } from 'react';

/**
 * Hook personalizado para scroll infinito com performance otimizada
 * 
 * @param {Object} options - Configurações do scroll infinito
 * @param {Function} options.loadMore - Função para carregar mais itens
 * @param {boolean} options.hasMore - Se há mais itens para carregar
 * @param {boolean} options.isLoading - Se está carregando atualmente
 * @param {number} options.threshold - Distância em pixels do final para trigger (default: 100)
 * @param {number} options.throttleMs - Throttle para scroll events (default: 100)
 * @param {string} options.direction - Direção do scroll: 'down' | 'up' (default: 'down')
 * @param {string} options.strategy - Estratégia: 'scroll' | 'intersection' (default: 'scroll')
 * @param {string} options.containerSelector - Seletor para container customizado
 * @param {boolean} options.disabled - Desabilitar o scroll infinito
 */
const useInfiniteScroll = ({
  loadMore,
  hasMore = true,
  isLoading = false,
  threshold = 100,
  throttleMs = 100,
  direction = 'down',
  strategy = 'scroll',
  containerSelector = null,
  disabled = false
}) => {
  const containerRef = useRef(null);
  const [error, setError] = useState(null);
  const loadMoreRef = useRef(loadMore);
  const throttleTimeoutRef = useRef(null);
  const intersectionObserverRef = useRef(null);
  const triggerElementRef = useRef(null);

  // Manter loadMore atualizado sem quebrar useEffect
  loadMoreRef.current = loadMore;

  // Função throttled para scroll
  const throttledCallback = useCallback((callback) => {
    if (throttleTimeoutRef.current) return;
    
    throttleTimeoutRef.current = setTimeout(() => {
      callback();
      throttleTimeoutRef.current = null;
    }, throttleMs);
  }, [throttleMs]);

  // Estratégia de Scroll Events
  const handleScroll = useCallback((e) => {
    if (disabled || isLoading || !hasMore) return;

    const target = e.target;
    const { scrollTop, scrollHeight, clientHeight } = target;

    let shouldLoad = false;

    if (direction === 'down') {
      // Scroll para baixo (carregamento no final)
      shouldLoad = scrollHeight - scrollTop - clientHeight <= threshold;
    } else {
      // Scroll para cima (carregamento no início)
      shouldLoad = scrollTop <= threshold;
    }

    if (shouldLoad) {
      throttledCallback(async () => {
        if (!isLoading && hasMore) {
          try {
            setError(null);
            await loadMoreRef.current();
          } catch (err) {
            setError(err.message || 'Erro ao carregar mais itens');
            console.error('InfiniteScroll Error:', err);
          }
        }
      });
    }
  }, [disabled, isLoading, hasMore, threshold, direction, throttledCallback]);

  // Estratégia de Intersection Observer
  const setupIntersectionObserver = useCallback(() => {
    if (!triggerElementRef.current || disabled) return;

    const options = {
      root: containerRef.current,
      rootMargin: `${threshold}px`,
      threshold: 0.1
    };

    intersectionObserverRef.current = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting && !isLoading && hasMore && !disabled) {
          throttledCallback(async () => {
            try {
              setError(null);
              await loadMoreRef.current();
            } catch (err) {
              setError(err.message || 'Erro ao carregar mais itens');
              console.error('InfiniteScroll Error:', err);
            }
          });
        }
      },
      options
    );

    intersectionObserverRef.current.observe(triggerElementRef.current);
  }, [disabled, isLoading, hasMore, threshold, throttledCallback]);

  // Setup dos event listeners
  useEffect(() => {
    if (disabled) return;

    const container = containerSelector 
      ? document.querySelector(containerSelector)
      : containerRef.current;

    if (!container) return;

    // Para PerfectScrollbar, buscar o container interno correto
    let scrollContainer = container;
    
    if (container.classList.contains('ps') || container.querySelector('.ps')) {
      scrollContainer = container.querySelector('.ps') || 
                      container.querySelector('.ps__rail-y')?.parentElement ||
                      container;
    }

    if (strategy === 'scroll') {
      scrollContainer.addEventListener('scroll', handleScroll, { passive: true });
      
      return () => {
        scrollContainer.removeEventListener('scroll', handleScroll);
        if (throttleTimeoutRef.current) {
          clearTimeout(throttleTimeoutRef.current);
          throttleTimeoutRef.current = null;
        }
      };
    } else if (strategy === 'intersection') {
      setupIntersectionObserver();
      
      return () => {
        if (intersectionObserverRef.current) {
          intersectionObserverRef.current.disconnect();
          intersectionObserverRef.current = null;
        }
        if (throttleTimeoutRef.current) {
          clearTimeout(throttleTimeoutRef.current);
          throttleTimeoutRef.current = null;
        }
      };
    }
  }, [strategy, handleScroll, setupIntersectionObserver, containerSelector, disabled]);

  // Cleanup geral
  useEffect(() => {
    return () => {
      if (throttleTimeoutRef.current) {
        clearTimeout(throttleTimeoutRef.current);
      }
      if (intersectionObserverRef.current) {
        intersectionObserverRef.current.disconnect();
      }
    };
  }, []);

  // Função para resetar erros
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Função para retry manual
  const retry = useCallback(async () => {
    if (isLoading) return;
    
    try {
      setError(null);
      await loadMoreRef.current();
    } catch (err) {
      setError(err.message || 'Erro ao carregar mais itens');
    }
  }, [isLoading]);

  return {
    containerRef,
    triggerElementRef, // Para usar com Intersection Observer
    error,
    clearError,
    retry
  };
};

export default useInfiniteScroll;

